/**
 * Aviso de "conta desativada" entre o pedido de exclusão e o ecrã de login (T1.17).
 *
 * Existe porque o pedido termina a sessão: o guard privado desmonta a página de exclusão e leva a
 * pessoa ao login antes de a página conseguir navegar com `state`. O aviso atravessa essa troca em
 * `sessionStorage` — só neste separador, sem PII (apenas a data), e é consumido uma única vez.
 */

interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
  removeItem(key: string): unknown;
}

export interface AccountDeletionNotice {
  deletionDueAt: string;
}

export const ACCOUNT_DELETION_NOTICE_KEY = 'powerful-connect:account:deletion-notice:v1';

function browserSessionStorage(): NoticeStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function parse(serialized: string | null): AccountDeletionNotice | null {
  if (!serialized) return null;
  try {
    const value = JSON.parse(serialized) as Record<string, unknown>;
    return typeof value.deletionDueAt === 'string' && Number.isFinite(Date.parse(value.deletionDueAt))
      ? { deletionDueAt: value.deletionDueAt }
      : null;
  } catch {
    return null;
  }
}

export function createAccountDeletionNoticeStore(storage: NoticeStorage | null = browserSessionStorage()) {
  // Fallback em memória: armazenamento bloqueado (modo privado) não pode fazer o aviso desaparecer.
  let memoryValue: string | null = null;

  return {
    write(notice: AccountDeletionNotice): void {
      memoryValue = JSON.stringify({ deletionDueAt: notice.deletionDueAt });
      try {
        storage?.setItem(ACCOUNT_DELETION_NOTICE_KEY, memoryValue);
      } catch {
        // O valor em memória continua disponível.
      }
    },

    /**
     * Leitura sem efeito colateral. Ler e apagar são passos separados de propósito: o StrictMode
     * chama inicializadores de estado duas vezes, e um "consume" na primeira deixaria a segunda —
     * a que o React pode usar — sem aviso.
     */
    read(): AccountDeletionNotice | null {
      let serialized = memoryValue;
      try {
        serialized = storage?.getItem(ACCOUNT_DELETION_NOTICE_KEY) ?? serialized;
      } catch {
        // Fica o valor em memória.
      }
      return parse(serialized);
    },

    /** Apaga depois de mostrado: o aviso aparece uma vez, não a cada visita ao login. */
    clear(): void {
      memoryValue = null;
      try {
        storage?.removeItem(ACCOUNT_DELETION_NOTICE_KEY);
      } catch {
        // O valor em memória já foi limpo.
      }
    },
  };
}

export const accountDeletionNoticeStore = createAccountDeletionNoticeStore();
